import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Timeline,
} from '@mantine/core'
import {
  ChevronDown,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  Circle,
  Mail,
} from 'lucide-react'
import { format } from 'date-fns'
import { MailEvents } from '@data/mail/model/mail'
import {
  useEffect,
  useState,
} from 'react'

interface EmailHistoryModalProps {
  opened: boolean
  onClose: () => void
  mailEvents: MailEvents[]
}

const eventColors: Record<string, string> = {
  created: 'gray',
  received: 'blue',
  processed: 'cyan',
  delivered: 'green',
  opened: 'teal',
  clicked: 'violet',
  bounced: 'red',
  dropped: 'red',
  deferred: 'orange',
  failed: 'red',
}

interface RecipientHistory {
  email: string
  events: MailEvents[]
}

export const EmailHistoryModal = ({ opened, onClose, mailEvents }: EmailHistoryModalProps) => {
  // State to track which recipients are expanded (all expanded by default)
  const [expandedRecipients, setExpandedRecipients] = useState<Set<number>>(new Set())

  if (mailEvents.length === 0) {
    return null
  }

  // Group events by email recipient
  const recipientHistoryMap = new Map<string, MailEvents[]>()

  mailEvents.forEach(event => {
    const existing = recipientHistoryMap.get(event.email) || []
    recipientHistoryMap.set(event.email, [...existing, event])
  })

  // Sort events within each recipient by date (most recent first)
  recipientHistoryMap.forEach((events, email) => {
    events.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  })

  const recipientHistories: RecipientHistory[] = Array.from(recipientHistoryMap.entries()).map(
    ([email, events]) => ({ email, events }),
  )

  // Initialize all recipients as expanded when modal opens
  useEffect(() => {
    if (opened) {
      const allIndices = new Set(recipientHistories.map((_, idx) => idx))
      setExpandedRecipients(allIndices)
    }
  }, [opened])

  const toggleRecipient = (idx: number) => {
    setExpandedRecipients(prev => {
      const newSet = new Set(prev)
      if (newSet.has(idx)) {
        newSet.delete(idx)
      } else {
        newSet.add(idx)
      }
      return newSet
    })
  }

  const expandAll = () => {
    const allIndices = new Set(recipientHistories.map((_, idx) => idx))
    setExpandedRecipients(allIndices)
  }

  const collapseAll = () => {
    setExpandedRecipients(new Set())
  }

  const allCollapsed = expandedRecipients.size === 0
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={'xs'}>
          <Mail size={20} color={'blue'} />
          <Text fw={600}>Full Email Transaction History</Text>
        </Group>
      }
      size={'lg'}
      centered
    >
      <Stack gap={'lg'}>
        <Group justify={'space-between'} align={'flex-start'}>
          <div>
            <Text size={'sm'} c={'dimmed'}>
              Complete delivery timeline for {recipientHistories.length} recipient
              {recipientHistories.length > 1 ? 's' : ''}
            </Text>
            <Text size={'xs'} c={'dimmed'} mt={4}>
              Total Events: {mailEvents.length}
            </Text>
          </div>
          <Button
            size={'xs'}
            variant={'light'}
            leftSection={
              allCollapsed ? <ChevronsDown size={14} /> : <ChevronsUp size={14} />
            }
            onClick={allCollapsed ? expandAll : collapseAll}
          >
            {allCollapsed ? 'Expand All' : 'Collapse All'}
          </Button>
        </Group>

        <Divider />

        <Stack gap={'xl'}>
          {recipientHistories.map((recipient, idx) => {
            const isExpanded = expandedRecipients.has(idx)

            return (
              <Paper key={idx} p={'md'} withBorder>
                <Group
                  justify={'space-between'}
                  mb={isExpanded ? 'md' : 0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleRecipient(idx)}
                >
                  <Group gap={'xs'}>
                    <Mail size={16} color={'blue'} />
                    <Text size={'sm'} fw={600}>
                      {recipient.email}
                    </Text>
                    <Badge size={'sm'} variant={'light'} color={'blue'}>
                      {recipient.events.length} event{recipient.events.length > 1 ? 's' : ''}
                    </Badge>
                  </Group>
                  <ActionIcon variant={'subtle'} size={'sm'} color={'blue'}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </ActionIcon>
                </Group>

                <Collapse expanded={isExpanded}>
                  <Timeline active={recipient.events.length} bulletSize={20} lineWidth={2}>
                    {recipient.events.map((event, eventIdx) => (
                      <Timeline.Item
                        key={eventIdx}
                        bullet={<Circle size={10} />}
                        title={
                          <Group gap={'xs'}>
                            <Badge
                              size={'sm'}
                              variant={'filled'}
                              color={eventColors[event.event] || 'gray'}
                            >
                              {event.event}
                            </Badge>
                          </Group>
                        }
                      >
                        <Text size={'xs'} c={'dimmed'} mt={4}>
                          {format(new Date(event.createdAt), 'dd-MM-yyyy HH:mm:ss')}
                        </Text>
                        <Text size={'xs'} c={'dimmed'} mt={2}>
                          Template: {event.templateId}
                        </Text>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </Collapse>
              </Paper>
            )
          })}
        </Stack>
      </Stack>
    </Modal>
  )
}
